import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices, Logger } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import React from 'react';
import { App } from './app';
import { HistoryServiceV2 } from '@feature-hub/history-service';
import { ServerRequestV1 } from '@feature-hub/server-request';
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ApiKeyProviderV1 } from '@volkswagen-onehub/api-key-provider';
import { DisclaimerManagerV1 } from '@volkswagen-onehub/disclaimer-manager';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { LayerManagerV24 } from '@volkswagen-onehub/layer-manager';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { PageInfoServiceV1 } from '@volkswagen-onehub/page-info-service';
import { BreadcrumbServiceV1 } from '@volkswagen-onehub/breadcrumb-service';
import { ServiceConfigEndpointProvider } from '@volkswagen-onehub/service-config-endpoint-provider';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';
import { PortalServiceV1 } from '@esportal/portal-service';
import { Environment, EnvironmentEnum } from './enviroment';
import { AuthServiceProviderV2, IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { UserInteractionServiceV22 } from '@volkswagen-onehub/user-interaction-service';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { ProfileServiceV2Impl, UserService } from '@vwdfive/myvw-services';
import { ThemeProvider } from '@volkswagen-onehub/components-core';
import { IMastersContext, MastersContext } from './context/masters-context';

export interface MastersFeatureAppDependencies extends FeatureServices {
	readonly 'vw:authService': AuthServiceProviderV2;
	readonly 'tracking': TrackingManagerV1;
	readonly 'es:portal-feature-service': PortalServiceV1;
	readonly 'service-config-provider': ServiceConfigProviderV1;
	readonly 'user-interaction-service'?: UserInteractionServiceV22;
	readonly 'disclaimer-manager': DisclaimerManagerV1;
	readonly 'layer-manager': LayerManagerV24;
	readonly 'locale-service': LocaleServiceV1;
	readonly 'navigation-service': NavigationServiceV1;
	readonly 's2:history': HistoryServiceV2;
	readonly 's2:serialized-state-manager': SerializedStateManagerV1;
	readonly 'service-config-endpoint-provider': ServiceConfigEndpointProvider;
	readonly 'featureapp-service': FeatureappServiceV1;
	readonly 'page-info-service': PageInfoServiceV1;
	readonly 'breadcrumb-service': BreadcrumbServiceV1;
	readonly 'api-key-provider'?: ApiKeyProviderV1;
	readonly 'fa-content-service'?: FaContentServiceV1;
	readonly 's2:async-ssr-manager': AsyncSsrManagerV1;
	readonly 's2:logger'?: Logger;
	readonly 's2:server-request'?: ServerRequestV1;
}

export const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, MastersFeatureAppDependencies> = {
	dependencies: {
		featureServices: {
			['vw:authService']: '2.0.0',
			'tracking': '^1.0.0',
			'es:portal-feature-service': '^1.0.0',
			'service-config-provider': '1.0.0',
			'user-interaction-service': '^2.2.0',
			'disclaimer-manager': '^1.1.0',
			'layer-manager': '^2.4.0',
			'locale-service': '^1.0.0',
			'navigation-service': '^1.1.0',
			's2:history': '^3.0.0',
			's2:serialized-state-manager': '^1.0.0',
			'service-config-endpoint-provider': '^1.0.0',
			'featureapp-service': '1.4.0',
		},
		externals: {
			'react': '^18.3.1',
			'@feature-hub/react': '^3.4.0',
		},
	},

	optionalDependencies: {
		featureServices: {
			'page-info-service': '^1.0.0',
			'breadcrumb-service': '^1.0.0',
			'api-key-provider': '^1.0.0',
			'fa-content-service': '^1.0.0',
			's2:async-ssr-manager': '^1.0.0',
			's2:logger': '^1.0.0',
			's2:server-request': '^1.0.0',
		},
	},

	create: (env: FeatureAppEnvironment<FeatureServices, unknown>) => {
		console.log('🚀 ~ env', env);

		const featureAppId = env.featureAppId;
		const featureServices = env.featureServices;

		if (env.config) {
			Environment.getInstance((env.config as { environment: EnvironmentEnum }).environment);
		}

		const serviceConfigProvider = env.featureServices['service-config-provider'] as ServiceConfigProviderV1;

		const authServiceBuilder = env.featureServices['vw:authService'] as AuthServiceProviderV2;

		const authServiceMarketFlag: IAuthServiceV2 = authServiceBuilder.register('market');

		const profileService = new ProfileServiceV2Impl(
			authServiceMarketFlag,
			serviceConfigProvider.configs['customer-profile']!.urlOrigin,
		);
		const userService = new UserService(authServiceMarketFlag);
		const portalService = env.featureServices['es:portal-feature-service'] as PortalServiceV1;
		const layerManager = env.featureServices['layer-manager'] as LayerManagerV24;
		const contentService = env.featureServices['fa-content-service'] as FaContentServiceV1;
		const historyService = env.featureServices['s2:history'] as HistoryServiceV2;
		const pageInfoService = env.featureServices['page-info-service'] as PageInfoServiceV1;
		const disclaimerManager = env.featureServices['disclaimer-manager'] as DisclaimerManagerV1;
		const breacrumbService = env.featureServices['breadcrumb-service'] as BreadcrumbServiceV1;
		const navigationService = env.featureServices['navigation-service'] as NavigationServiceV1;
		const featureAppService = env.featureServices['featureapp-service'] as FeatureappServiceV1;
		const userInteractionService = featureServices['user-interaction-service'] as UserInteractionServiceV22;
		const trackingService = env.featureServices['tracking'];

		return {
			render: () => {
				return (
					<MastersContext.Provider
						value={
							{
								featureAppId,
								authService: authServiceMarketFlag,
								profileService,
								layerManager,
								userService,
								portalService,
								serviceConfigProvider,
								featureServices,
								historyService,
								breacrumbService,
								disclaimerManager,
								pageInfoService,
								navigationService,
								featureAppService,
								contentService,
								trackingService,
								userInteractionService,
							} as unknown as IMastersContext
						}
					>
						<ThemeProvider theme="main">
							<App />
						</ThemeProvider>
					</MastersContext.Provider>
				);
			},
		};
	},
};

export default featureAppDefinition;
